import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { DEFAULT_QUERY_STATUS, LOADING_STATUS, SUCCESS_STATUS } from 'state/constants'
import { FetchStatus } from 'state/types'
import { addPairToMarket, createMarket, fetchAllMarkets, fetchAllTokens, fetchMarketsByOwner } from './actions'
import { ManageState } from './types'

export const MANAGE_INITIAL_STATE: ManageState = {
	listPair: {
		quoteToken: '',
		baseToken: '',
		feeTier: 0,
		initialPrice: '',
	},
	customMarkets: {},
	allTokens: [],
	allMarkets: [],
	queryStatuses: {
		customMarkets: DEFAULT_QUERY_STATUS,
		allTokens: DEFAULT_QUERY_STATUS,
		allMarkets: DEFAULT_QUERY_STATUS,
	},
	writeStatuses: {
		createMarket: FetchStatus.Idle,
		addPairToMarket: FetchStatus.Idle,
	},
}

const manageSlice = createSlice({
	name: 'manage',
	initialState: MANAGE_INITIAL_STATE,
	reducers: {
		setBaseToken: (state, action: PayloadAction<string>) => {
			state.listPair.baseToken = action.payload
		},
		setQuoteToken: (state, action: PayloadAction<string>) => {
			state.listPair.quoteToken = action.payload
		},
		setInitialPrice: (state, action: PayloadAction<string>) => {
			state.listPair.initialPrice = action.payload
		},
		setFeeTier: (state, action: PayloadAction<number>) => {
			state.listPair.feeTier = action.payload
		},
		setCreateMarketStatus: (state, action: PayloadAction<FetchStatus>) => {
			state.writeStatuses.createMarket = action.payload
		},
		setAddPairToMarketStatus: (state, action: PayloadAction<FetchStatus>) => {
			state.writeStatuses.addPairToMarket = action.payload
		},
	},
	extraReducers: (builder) => {
		// Fetch custom markets
		builder.addCase(fetchMarketsByOwner.pending, (manageState) => {
			manageState.queryStatuses.customMarkets = LOADING_STATUS
		})
		builder.addCase(fetchMarketsByOwner.fulfilled, (manageState, action) => {
			manageState.customMarkets = action.payload
			manageState.queryStatuses.customMarkets = SUCCESS_STATUS
		})
		builder.addCase(fetchMarketsByOwner.rejected, (manageState) => {
			manageState.queryStatuses.customMarkets = {
				error: 'Failed to fetch custom markets',
				status: FetchStatus.Error,
			}
		})

		// Fetch All Tokens
		builder.addCase(fetchAllTokens.pending, (manageState) => {
			manageState.queryStatuses.allTokens = LOADING_STATUS
		})
		builder.addCase(fetchAllTokens.fulfilled, (manageState, action) => {
			manageState.allTokens = action.payload
			manageState.queryStatuses.customMarkets = SUCCESS_STATUS
		})
		builder.addCase(fetchAllTokens.rejected, (manageState) => {
			manageState.queryStatuses.allTokens = {
				error: 'Failed to fetch tokens listed in market',
				status: FetchStatus.Error,
			}
		})

		// Fetch All Markets
		builder.addCase(fetchAllMarkets.pending, (manageState) => {
			manageState.queryStatuses.allMarkets = LOADING_STATUS
		})
		builder.addCase(fetchAllMarkets.fulfilled, (manageState, action) => {
			manageState.allMarkets = action.payload
			manageState.queryStatuses.allMarkets = SUCCESS_STATUS
		})
		builder.addCase(fetchAllMarkets.rejected, (manageState) => {
			manageState.queryStatuses.allMarkets = {
				error: 'Failed to fetch pairs listed in market',
				status: FetchStatus.Error,
			}
		})

		// Write Statuses
		builder.addCase(createMarket.pending, (state) => {
			state.writeStatuses.createMarket = FetchStatus.Loading
		})
		builder.addCase(createMarket.rejected, (state) => {
			state.writeStatuses.createMarket = FetchStatus.Error
		})
		builder.addCase(addPairToMarket.pending, (state) => {
			state.writeStatuses.addPairToMarket = FetchStatus.Loading
		})
		builder.addCase(addPairToMarket.rejected, (state) => {
			state.writeStatuses.addPairToMarket = FetchStatus.Error
		})
	},
})

export const {
	setBaseToken,
	setQuoteToken,
	setInitialPrice,
	setFeeTier,
	setAddPairToMarketStatus,
} = manageSlice.actions

export default manageSlice.reducer
